import React, { useCallback, useMemo, useState } from 'react';

import { Confirm, Guideline, Modal } from '@components/common';

import { portalTasks as portalTasksSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { removeLinkedTask } from '@slices/portalTasks';

import { isEmpty, uuid } from '@utils/common';

import { IIdType } from '@interfaces/common/common';

import colors from '@constants/colors';
import { MODAL_WIDTH_480 } from '@constants/modals';
import { unlinkConfirmText } from './constants';

import LinkedTaskItem from './LinkedTaskItem';
import { getLinkedTasksGroups } from './utils';

import styles from './LinkedTasksList.module.scss';

const LinkedTasksList = ({ isViewMode }: { isViewMode: boolean }) => {
	const dispatch = useAppDispatch();

	const { linkedTasks } = useAppSelector(portalTasksSelector);

	const [isShowUnlinkConfirm, setIsShowUnlinkConfirm] = useState(false);
	const [taskForUnlink, setTaskForUnlink] = useState<IIdType | null>(null);

	const showUnlinkConfirm = useCallback(() => setIsShowUnlinkConfirm(true), []);

	const closeUnlinkConfirm = () => {
		setIsShowUnlinkConfirm(false);

		setTaskForUnlink(null);
	};

	const unlinkTask = () => {
		if (!taskForUnlink) return;

		dispatch(removeLinkedTask({
			id: taskForUnlink.id,
			type: taskForUnlink.type,
		}));

		setTaskForUnlink(null);
		closeUnlinkConfirm();
	};

	const getTaskDatesInfo = useCallback((startDate?: string, endDate?: string) => {
		if (!startDate && !endDate) return;

		return (startDate && endDate)
			? `${startDate} - ${endDate}`
			: startDate ? `Start date: ${startDate}` : `End date: ${endDate}`;
	}, []);

	const linkedTasksGroups = useMemo(() => getLinkedTasksGroups(linkedTasks), [linkedTasks]);

	return (
		<>
			{linkedTasksGroups.map(linkedTasksGroup => {
				if (isEmpty(linkedTasksGroup.linkedTasks)) return;

				return (
					<div
						key={uuid()}
						className={styles.linkedTasksList}
					>
						<div className={styles.titleContainer}>
							<div className={styles.title}>
								{linkedTasksGroup.title}
							</div>
							{linkedTasksGroup?.typeDefinitionText
								&& <Guideline
									guidelineText={linkedTasksGroup.typeDefinitionText}
									iconColor={colors.gray40}
									iconHeight='12'
									iconWidth='12'
									placement='bottom'
								/>
							}
						</div>
						{linkedTasksGroup.linkedTasks.map(task => {
							return (
								<LinkedTaskItem
									key={uuid()}
									getTaskDatesInfo={getTaskDatesInfo}
									isViewMode={isViewMode}
									linkedTask={task}
									setTaskForUnlink={setTaskForUnlink}
									showUnlinkConfirm={showUnlinkConfirm}
								/>
							);
						})}
					</div>
				);
			})}
			{isShowUnlinkConfirm &&
				<Modal
					isOpen={isShowUnlinkConfirm}
					onClose={closeUnlinkConfirm}
					title='Confirm action'
					width={MODAL_WIDTH_480}
				>
					<Confirm
						onClose={closeUnlinkConfirm}
						onSubmit={unlinkTask}
						question={unlinkConfirmText}
					/>
				</Modal>
			}
		</>
	);
};

export default LinkedTasksList;
